import React from 'react';
import {
  BarChart3,
  BookOpen,
  Cloud,
  CloudOff,
  Copy,
  FileEdit,
  FileText,
  Home,
  Info,
  Loader2,
  LogOut,
  Map,
  MenuSquare,
  MessageSquare,
  Moon,
  Settings,
  Sparkles,
  Sun,
  UserCog,
  Video,
  X,
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

import type { TabId } from './TabLayout';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
  userName?: string;
  onLogout: () => void;
  onHome: () => void;
  sessionTitle?: string;
  sharePermissions?: any;
  shareMetadata?: any;
  activeTabId?: string;
  onTabChange?: (tabId: TabId) => void;
  availableTabIds?: TabId[];
  selectedTtmlName?: string;
  hasVideoUrl?: boolean;
  isYoutubePlayerVisible?: boolean;
  onToggleYoutubePlayer?: () => void;
  hasSpeakers?: boolean;
  onEditSpeakers?: () => void;
  onOpenSettings?: () => void;
  isOfflineAvailable?: boolean;
  isOfflineMode?: boolean;
  isOfflineActionPending?: boolean;
  offlineActionStatusLabel?: string;
  onToggleOfflineAvailability?: () => Promise<void> | void;
  onCopyOfflineDiagnostics?: () => Promise<void> | void;
}

const TAB_DEFINITIONS: Array<{ id: TabId; label: string; icon: React.ReactNode }> = [
  { id: 'ttml', label: 'Ranking and Metadata', icon: <BarChart3 size={18} /> },
  { id: 'transcript', label: 'Timeline View', icon: <FileText size={18} /> },
  { id: 'readable', label: 'Transcript', icon: <BookOpen size={18} /> },
  { id: 'chat', label: 'Chat with Transcript', icon: <MessageSquare size={18} /> },
  { id: 'agent', label: 'Agent', icon: <Sparkles size={18} /> },
  { id: 'narrative', label: 'Topic Map', icon: <Map size={18} /> },
  { id: 'description', label: 'Description', icon: <FileEdit size={18} /> },
  { id: 'context', label: 'Context', icon: <MenuSquare size={18} /> },
  { id: 'questions', label: 'Questions', icon: <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full border border-current text-[10px] font-bold leading-none">Q</span> },
];

const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  onClose,
  isDarkMode,
  toggleDarkMode,
  userName,
  onLogout,
  onHome,
  sessionTitle,
  sharePermissions,
  shareMetadata,
  activeTabId,
  onTabChange,
  availableTabIds,
  selectedTtmlName,
  hasVideoUrl,
  isYoutubePlayerVisible,
  onToggleYoutubePlayer,
  hasSpeakers,
  onEditSpeakers,
  onOpenSettings,
  isOfflineAvailable = false,
  isOfflineMode = false,
  isOfflineActionPending = false,
  offlineActionStatusLabel,
  onToggleOfflineAvailability,
  onCopyOfflineDiagnostics,
}) => {
  const visibleTabs = TAB_DEFINITIONS.filter((tab) => !availableTabIds || availableTabIds.includes(tab.id));

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/55"
            onClick={onClose}
          />

          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.24 }}
            className={`fixed inset-y-0 right-0 z-50 flex w-[20rem] max-w-[86vw] flex-col shadow-2xl ${
              isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'
            }`}
            style={{
              paddingTop: 'var(--app-mobile-top-offset)',
              paddingBottom: 'var(--app-mobile-bottom-offset)',
            }}
          >
            <div className={`flex items-center justify-between border-b px-4 py-4 ${
              isDarkMode ? 'border-gray-800' : 'border-gray-200'
            }`}>
              <div>
                <h2 className="text-lg font-semibold">Session Menu</h2>
                <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Jump between tabs and session controls.
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className={`rounded-xl p-2 transition-colors ${
                  isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
                }`}
                title="Close menu"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {sessionTitle && (
                <div className={`rounded-2xl px-4 py-3 ${
                  isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
                }`}>
                  <div className={`text-xs uppercase tracking-[0.18em] ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Session
                  </div>
                  <div className="mt-2 text-sm font-semibold leading-6">{sessionTitle}</div>
                </div>
              )}

              {userName && !sharePermissions && (
                <div className={`mt-4 rounded-2xl px-4 py-3 text-sm ${
                  isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-100 text-gray-700'
                }`}>
                  Signed in as {userName}
                </div>
              )}

              {selectedTtmlName && (
                <div className={`mt-4 rounded-2xl px-4 py-3 text-sm ${
                  isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-100 text-gray-700'
                }`}>
                  Active metadata: {selectedTtmlName.replace(/\.ttml$/i, '')}
                </div>
              )}

              {sharePermissions && (
                <div className={`mt-4 rounded-2xl px-4 py-4 ${
                  isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-100 text-gray-700'
                }`}>
                  <div className="flex items-start gap-3">
                    <Info size={18} className="mt-0.5 shrink-0 text-brand-yellow" />
                    <div className="min-w-0">
                      <div className="font-medium">Public Share</div>
                      {shareMetadata?.label && (
                        <div className={`mt-1 text-xs ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                          {shareMetadata.label}
                        </div>
                      )}
                      <div className={`mt-2 space-y-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {shareMetadata?.expiresAt && (
                          <div>Expires {new Date(shareMetadata.expiresAt).toLocaleString()}</div>
                        )}
                        {shareMetadata?.maxViewers && (
                          <div>Viewers {shareMetadata.currentViewers || 0} / {shareMetadata.maxViewers}</div>
                        )}
                        {shareMetadata?.maxTotalViews && (
                          <div>Views {shareMetadata.totalViews || 0} / {shareMetadata.maxTotalViews}</div>
                        )}
                      </div>
                      <div className={`mt-3 flex flex-wrap gap-2 text-[11px] font-medium ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        <span className={`rounded-full px-2.5 py-1 ${
                          isDarkMode ? 'bg-gray-700' : 'bg-white'
                        }`}>
                          Transcript {sharePermissions.transcriptsEnabled ? 'On' : 'Off'}
                        </span>
                        <span className={`rounded-full px-2.5 py-1 ${
                          isDarkMode ? 'bg-gray-700' : 'bg-white'
                        }`}>
                          Comments {sharePermissions.commentsRead ? 'On' : 'Off'}
                        </span>
                        {sharePermissions.rankingEnabled && (
                          <span className={`rounded-full px-2.5 py-1 ${
                            isDarkMode ? 'bg-gray-700' : 'bg-white'
                          }`}>
                            Topic Map On
                          </span>
                        )}
                        {sharePermissions.questionsEnabled && (
                          <span className={`rounded-full px-2.5 py-1 ${
                            isDarkMode ? 'bg-gray-700' : 'bg-white'
                          }`}>
                            Questions On
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6">
                <div className={`px-1 text-xs font-semibold uppercase tracking-[0.18em] ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Navigation
                </div>
                <div className="mt-3 space-y-2">
                  {visibleTabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => {
                        onTabChange?.(tab.id);
                        onClose();
                      }}
                      className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm transition-colors ${
                        activeTabId === tab.id
                          ? isDarkMode
                            ? 'bg-primary-600 text-white'
                            : 'bg-primary-100 text-primary-700'
                          : isDarkMode
                            ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {tab.icon}
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <div className={`px-1 text-xs font-semibold uppercase tracking-[0.18em] ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Actions
                </div>
                <div className="mt-3 space-y-2">
                  {!sharePermissions && onOpenSettings && (
                    <button
                      type="button"
                      onClick={() => {
                        onOpenSettings();
                        onClose();
                      }}
                      className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm transition-colors ${
                        isDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Settings size={18} />
                      <span>Session Settings</span>
                    </button>
                  )}

                  {onToggleOfflineAvailability && (
                    <button
                      type="button"
                      onClick={() => {
                        void onToggleOfflineAvailability();
                      }}
                      disabled={isOfflineActionPending}
                      className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm transition-colors ${
                        isDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      } ${isOfflineActionPending ? 'cursor-wait opacity-70' : ''}`}
                    >
                      {isOfflineActionPending ? (
                        <Loader2 size={18} className="animate-spin" />
                      ) : isOfflineAvailable ? (
                        <CloudOff size={18} />
                      ) : (
                        <Cloud size={18} />
                      )}
                      <div className="min-w-0">
                        <div>
                          {isOfflineActionPending
                            ? 'Updating Offline Copy'
                            : isOfflineAvailable
                              ? 'Remove Offline Copy'
                              : 'Make Available Offline'}
                        </div>
                        {isOfflineActionPending && offlineActionStatusLabel && (
                          <div className={`mt-0.5 text-xs ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                            {offlineActionStatusLabel}
                          </div>
                        )}
                      </div>
                    </button>
                  )}

                  {onCopyOfflineDiagnostics && (
                    <button
                      type="button"
                      onClick={() => {
                        void onCopyOfflineDiagnostics();
                      }}
                      className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm transition-colors ${
                        isDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Copy size={18} />
                      <div className="min-w-0">
                        <div>Copy Offline Diagnostics</div>
                        <div className={`mt-0.5 text-xs ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Copies the recent offline save log so you can paste it here.
                        </div>
                      </div>
                    </button>
                  )}

                  {hasVideoUrl && onToggleYoutubePlayer && (
                    <button
                      type="button"
                      onClick={() => {
                        onToggleYoutubePlayer();
                      }}
                      className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm transition-colors ${
                        isDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <Video size={18} />
                      <span>{isYoutubePlayerVisible ? 'Hide Video' : 'Show Video'}</span>
                    </button>
                  )}

                  {!sharePermissions && hasSpeakers && onEditSpeakers && (
                    <button
                      type="button"
                      onClick={() => {
                        onEditSpeakers();
                        onClose();
                      }}
                      className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm transition-colors ${
                        isDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <UserCog size={18} />
                      <span>Edit Speaker Names</span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      toggleDarkMode();
                    }}
                    className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm transition-colors ${
                      isDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
                    <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      onHome();
                      onClose();
                    }}
                    className={`flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm transition-colors ${
                      isDarkMode ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Home size={18} />
                    <span>{sharePermissions ? 'All Videos' : 'Home'}</span>
                  </button>

                  {!sharePermissions && (
                    <button
                      type="button"
                      onClick={() => {
                        onLogout();
                        onClose();
                      }}
                      className="flex w-full items-center gap-3 rounded-2xl bg-red-50 px-4 py-3 text-left text-sm text-red-600 transition-colors hover:bg-red-100 dark:bg-red-950/40 dark:text-red-300 dark:hover:bg-red-950/60"
                    >
                      <LogOut size={18} />
                      <span>Logout</span>
                    </button>
                  )}
                </div>
              </div>

              {isOfflineMode && (
                <div className={`mt-4 rounded-2xl px-4 py-3 text-sm ${
                  isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                }`}>
                  Offline copy loaded. Video, chat, editing, and server actions are unavailable.
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MobileMenu;
